<template>
  <div v-if="selected || slots['mobile-header-append']" class="d-flex flex-wrap align-center row-gap-2 mb-3 ml-4">
    <e-checkbox
      v-if="selected"
      :model-value="isAllSelected"
      :square-selected="isPartialSelected"
      :disabled="disabled || loading"
      label="Выбрать все"
      @update:model-value="onAllSelectedUpdate"
    />
    <v-spacer />
    <slot name="mobile-header-append" />
  </div>

  <template v-if="loading">
    <skeleton-loader v-for="item in 10" height="160px" class="mb-4" :key="item" />
  </template>
  <template v-else>
    <div
      v-for="(item, itemIndex) in items"
      :key="itemIndex"
      class="mobile-list-item"
      v-bind="applyRowProps({ item })"
      @click="onRowClick($event, { item, index: itemIndex })"
    >
      <div v-if="selected" class="mb-3">
        <slot
          name="item.data-table-select"
          :is-selected="isSelected"
          :internal-item="item"
          :item="item"
          :toggle-select="onSelectUpdate"
        >
          <e-checkbox
            :model-value="isSelected(item)"
            :disabled="disabled"
            @update:model-value="onSelectUpdate(item, $event)"
          />
        </slot>
      </div>
      <div class="d-flex">
        <div class="w-100">
          <template v-for="(row, rowIndex) in headers">
            <div
              v-if="row.key && row.key !== 'dataItemSecondRow' && row.key !== 'actions'"
              :key="rowIndex"
              class="mobile-list-item__row"
            >
              <div v-if="row.title" class="d-flex align-center th text-ordinary">
                {{ row.title }}
              </div>
              <div class="td text-secondary">
                <template v-if="!$slots[`item.${row.key}`]">
                  {{ getNestedObjectProperty(item, row.key) }}
                </template>
                <slot
                  :name="`item.${row.key}`"
                  :item="item"
                  :internal-item="item"
                  :is-selected="isSelected"
                  :value="getNestedObjectProperty(item, row.key)"
                />
              </div>
            </div>
          </template>
        </div>
        <slot
          name="item.actions"
          :item="item"
          :internal-item="item"
          :is-selected="isSelected"
          :value="getNestedObjectProperty(item, 'actions')"
        />
      </div>
      <div v-if="$slots['item.dataItemSecondRow']" class="mobile-list-item__footer">
        <slot
          name="item.dataItemSecondRow"
          :item="item"
          :is-selected="isSelected"
          :internal-item="item"
          :value="getNestedObjectProperty(item, 'dataItemSecondRow')"
        />
      </div>
    </div>
  </template>
</template>

<script setup lang="ts" generic="T extends Record<string, unknown>">
import { toRef } from 'vue';
import type { EDataItemsOnRowClickItem } from '@/core/types/components/components';
import type { DataTableHeaders, RowProps, TableSlots } from '@/core/types/components/table';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { ECheckbox } from '@/core/components/ui';
import { getNestedObjectProperty } from '@/core/utils/object';
import { isObject } from '@/core/utils/type-guards';

const props = defineProps<{
  headers: DataTableHeaders;
  items: T[];
  disabled?: boolean;
  itemValue?: string;
  returnObject?: boolean;
  rowProps?: RowProps<T>;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (event: 'onRowClick', e: Event, row: EDataItemsOnRowClickItem<T>): void;
}>();

const slots = defineSlots<TableSlots<T>>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const selected = defineModel<any[]>();

const isAllSelected = toRef(() => selected.value?.length === props.items.length);
const isPartialSelected = toRef(() => !!selected.value?.length);

const applyRowProps = (data: { item: T }) => {
  const rProps = isObject(props.rowProps)
    ? props.rowProps
    : typeof props.rowProps === 'function'
      ? props.rowProps(data)
      : {};

  return rProps;
};

const isSelected = (item: T) => {
  if (!selected.value) {
    return false;
  }

  if (props.itemValue) {
    return selected.value.some((s) => s === getNestedObjectProperty(item, props.itemValue!));
  }

  return selected.value.some((s) => s === item);
};

const onSelectUpdate = (item: T, status: boolean) => {
  if (!selected.value) {
    return;
  }

  if (props.itemValue) {
    if (status) {
      selected.value = selected.value.concat([getNestedObjectProperty(item, props.itemValue)]);
    } else {
      selected.value = selected.value.filter(
        (selectedItem) => selectedItem !== getNestedObjectProperty(item, props.itemValue!),
      );
    }

    return;
  }

  if (status) {
    selected.value = selected.value.concat([item]);
  } else {
    selected.value = selected.value.filter((selectedItem) => selectedItem !== item);
  }
};

const onAllSelectedUpdate = (status: boolean) => {
  if (status || (!status && !isAllSelected.value)) {
    if (props.itemValue) {
      selected.value = [...props.items.map((item) => getNestedObjectProperty(item, props.itemValue!))];
    } else {
      selected.value = [...props.items];
    }
  } else {
    if (isAllSelected.value) {
      selected.value = [];
    }
  }
};

const onRowClick = (e: Event, row: { item: T; index: number }) => {
  emit('onRowClick', e, row);
};
</script>

<style scoped lang="scss">
.mobile-list-item {
  padding: $gap-4;

  border-radius: $border-radius;

  &.disable-loader {
    @include disable-loader;
  }

  &:nth-of-type(2n + 1) {
    background-color: $bg-light-color;
  }

  &__row {
    display: flex;

    &:not(:first-child) {
      margin-top: $gap-3;
    }

    & > div {
      width: 50%;
    }
  }

  &__footer {
    &:not(:empty) {
      margin-top: 8px;
    }
  }
}
</style>
